import React from "react";
import {
  BlockText,
  BlockFigure,
  ImageSequence,
  RecipeBoard,
  RecipeRow,
  RecipeCell,
  blockSectionBodyWrapClass,
} from "../../../designSystem/projectBlocks";

export const meta = {
  slug: "a-small-tool",
  title: "A small tool.",
  summary:
    "Notes on building a Grasshopper component that voxelises arbitrary geometry into a clean assembly — and what fell out of the process.",
  date: "2026-03-01",
  tags: ["grasshopper", "tooling"],
};

const APE_BASE = "/projects/APETools/images";
const VOXEL_FRAMES = [
  `${APE_BASE}/Magica Voxel.PNG`,
  `${APE_BASE}/Rhino Viewport.PNG`,
  `${APE_BASE}/APE Panel.PNG`,
  `${APE_BASE}/Capture.PNG`,
];
const VOXEL_CAPS = [
  "Source mesh",
  "Voxel output",
  "APE panel",
  "Grasshopper graph",
];

export default function Entry() {
  return (
    <>
      <BlockText
        className={blockSectionBodyWrapClass}
        paragraphs={[
          "I have been carrying around a one-component voxeliser for years — a small tool that turns any input geometry into a tight assembly of voxels. The first version was a single Grasshopper definition; the latest is a packaged APE component with a proper panel.",
          "Three things surprised me while rewriting it. One: most of the runtime is in the sampling, not the meshing. Two: caching by the input mesh’s bounding box hash makes interactive parameter sweeps feel instant. Three: the right default voxel size is almost always wrong — the tool should expose three: coarse / medium / fine — and let the designer adjust from there.",
        ]}
      />

      {/* Sequence — autoplay-paused player with control bar */}
      <RecipeBoard>
        <RecipeRow>
          <RecipeCell span={12} className="!p-0 !border-r-0">
            <ImageSequence
              images={VOXEL_FRAMES}
              captions={VOXEL_CAPS}
              label="Voxeliser · pipeline"
              ratio="video"
            />
          </RecipeCell>
        </RecipeRow>
      </RecipeBoard>

      <BlockText
        className={blockSectionBodyWrapClass}
        paragraphs={[
          "The other thing the rewrite forced me to confront was packaging. A tool that lives only in one Grasshopper file is a tool one person can use; a tool with a clean UI panel, named inputs, and tooltips is a tool a team can use. Below: input mesh on the left, voxelised output on the right.",
        ]}
      />

      {/* Side-by-side — same ratio, same fit */}
      <RecipeBoard>
        <RecipeRow>
          <RecipeCell span={6} className="!p-0">
            <BlockFigure
              src={`${APE_BASE}/Magica Voxel.PNG`}
              alt="Voxeliser — source mesh"
              ratio="classic"
              fit="cover"
              caption="Input mesh"
            />
          </RecipeCell>
          <RecipeCell span={6} className="!p-0 !border-r-0">
            <BlockFigure
              src={`${APE_BASE}/Rhino Viewport.PNG`}
              alt="Voxeliser — output assembly"
              ratio="classic"
              fit="cover"
              caption="Voxel output"
            />
          </RecipeCell>
        </RecipeRow>
      </RecipeBoard>

      <BlockText
        className={blockSectionBodyWrapClass}
        paragraphs={[
          "I would like to follow this with a one-component slicer next, on the same chassis. We'll see how that goes.",
        ]}
      />
    </>
  );
}
